import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import {
  Users,
  Home,
  Building2,
  UserCheck,
  TrendingUp,
  IndianRupee,
  CheckCircle2,
} from "lucide-react";
import { AnimatedCounter } from "./AnimatedCounter";

const ecosystemNodes = [
  { icon: Home, label: "Owner\nProperties", angle: -90 }, // Top
  { icon: Users, label: "Brokers", angle: 0 }, // Right
  { icon: UserCheck, label: "Agent\nListings", angle: 90 }, // Bottom
  { icon: Building2, label: "Builder\nProjects", angle: 180 }, // Left
];

const stats = [
  { icon: Users, number: 40000, suffix: "+", label: "Owners Registered" },
  { icon: Home, number: "30Cr", suffix: "+", label: "Worth Market Deal Done" },
  { icon: Building2, number: 500, suffix: "+", label: "Active Brokers" },
];

const benefits = [
  { text: "Access thousands of verified owner properties." },
  { text: "Receive direct inquiries from property owners." },
  { text: "Close deals faster with a powerful broker network." },
];

const EcosystemAndCommissionSection = () => {
  const sectionRef = useRef(null);
  const ecosystemRef = useRef(null);
  const statsRef = useRef(null);
  const growthRef = useRef(null);

  const ecosystemInView = useInView(ecosystemRef, {
    once: true,
    margin: "-100px",
  });
  const statsInView = useInView(statsRef, { once: true, margin: "-50px" });
  const growthInView = useInView(growthRef, { once: true, margin: "-80px" });

  const size = 320;
  const c = size / 2; // 160 - exact center
  const r = 120; // orbit radius
  const nodeSize = 64; // w-16 = 64px

  return (
    <section
      ref={sectionRef}
      id="network"
      className="py-20 sm:py-28 section-light"
    >
      <div className="section-light container mx-auto px-4 sm:px-6">
        {/* Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground mb-2">
            Network
          </p>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-foreground">
            One Platform for the{" "}
            <span className="gradient-text font-bold">Entire Network</span>
          </h2>
        </motion.div>

        {/* Ecosystem Diagram - flexbox centered */}
        <div
          ref={ecosystemRef}
          className="flex items-center justify-center mb-20"
        >
          <div className="relative" style={{ width: size, height: size }}>
            {/* Orbit circle */}
            <svg
              className="absolute inset-0 w-full h-full"
              viewBox={`0 0 ${size} ${size}`}
            >
              <circle
                cx={c}
                cy={c}
                r={r}
                fill="none"
                stroke="hsl(var(--primary))"
                strokeWidth="1"
                strokeDasharray="6 6"
                opacity="0.15"
              />
              <circle
                cx={c}
                cy={c}
                r={r - 30}
                fill="none"
                stroke="hsl(var(--primary))"
                strokeWidth="1"
                opacity="0.08"
              />

              {/* Connecting dashed lines + arrows */}
              <defs>
                <marker
                  id="eco-arrow"
                  markerWidth="8"
                  markerHeight="6"
                  refX="8"
                  refY="3"
                  orient="auto"
                >
                  <polygon
                    points="0 0, 8 3, 0 6"
                    fill="hsl(var(--primary))"
                    opacity="0.5"
                  />
                </marker>
              </defs>
              {ecosystemNodes.map((node, i) => {
                const rad = (node.angle * Math.PI) / 180;
                const nx = c + r * Math.cos(rad);
                const ny = c + r * Math.sin(rad);
                const midX = c + 50 * Math.cos(rad);
                const midY = c + 50 * Math.sin(rad);
                return (
                  <g key={i}>
                    <motion.line
                      x1={c}
                      y1={c}
                      x2={nx}
                      y2={ny}
                      stroke="hsl(var(--primary))"
                      strokeWidth="1"
                      strokeDasharray="4 4"
                      initial={{ pathLength: 0, opacity: 0 }}
                      animate={
                        ecosystemInView ? { pathLength: 1, opacity: 0.2 } : {}
                      }
                      transition={{ delay: 0.8 + i * 0.1, duration: 0.6 }}
                    />
                    <motion.line
                      x1={nx}
                      y1={ny}
                      x2={midX}
                      y2={midY}
                      stroke="hsl(var(--primary))"
                      strokeWidth="1.5"
                      markerEnd="url(#eco-arrow)"
                      initial={{ opacity: 0 }}
                      animate={ecosystemInView ? { opacity: 0.4 } : {}}
                      transition={{ delay: 1.4 + i * 0.1, duration: 0.4 }}
                    />
                  </g>
                );
              })}
            </svg>

            {/* Center Hub - absolutely centered with transform */}
            <motion.div
              className="absolute z-10 w-24 h-24 rounded-full bg-primary flex items-center justify-center shadow-lg"
              style={{
                left: c - 48,
                top: c - 48,
                boxShadow: "0 0 40px hsl(var(--primary) / 0.3)",
              }}
              initial={{ scale: 0, opacity: 0 }}
              animate={ecosystemInView ? { scale: 1, opacity: 1 } : {}}
              transition={{ duration: 0.5, type: "spring" }}
            >
              <span className="text-primary-foreground font-bold text-xs">
                PRObroker
              </span>
            </motion.div>

            {/* Orbiting Nodes - positioned from center */}
            {ecosystemNodes.map((node, i) => {
              const rad = (node.angle * Math.PI) / 180;
              const nodeLeft = c + r * Math.cos(rad) - nodeSize / 2;
              const nodeTop = c + r * Math.sin(rad) - nodeSize / 2;
              const Icon = node.icon;

              const entryX = Math.cos(rad) * 100;
              const entryY = Math.sin(rad) * 100;

              return (
                <motion.div
                  key={i}
                  className="absolute w-16 h-16 rounded-full bg-background border border-border shadow-md flex flex-col items-center justify-center z-10"
                  style={{ left: nodeLeft, top: nodeTop }}
                  initial={{ x: entryX, y: entryY, opacity: 0, scale: 0.5 }}
                  animate={
                    ecosystemInView ? { x: 0, y: 0, opacity: 1, scale: 1 } : {}
                  }
                  transition={{
                    delay: 0.3 + i * 0.15,
                    duration: 0.7,
                    type: "spring",
                    stiffness: 80,
                  }}
                >
                  <Icon className="w-5 h-5 text-primary mb-0.5" />
                  <span className="text-[7px] font-semibold text-foreground text-center leading-tight whitespace-pre-line">
                    {node.label}
                  </span>
                </motion.div>
              );
            })}

            {/* Rotating dots */}
            <motion.div
              className="absolute inset-0"
              animate={{ rotate: 360 }}
              transition={{ duration: 60, repeat: Infinity, ease: "linear" }}
            >
              {[0, 45, 90, 135, 180, 225, 270, 315].map((deg) => {
                const dotR = r + 20;
                const rad = (deg * Math.PI) / 180;
                return (
                  <div
                    key={deg}
                    className="absolute w-1.5 h-1.5 rounded-full bg-primary/15"
                    style={{
                      left: c + dotR * Math.cos(rad) - 3,
                      top: c + dotR * Math.sin(rad) - 3,
                    }}
                  />
                );
              })}
            </motion.div>
          </div>
        </div>

        {/* Statistics Row */}
        <div
          ref={statsRef}
          className="grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-4xl mx-auto mb-24"
        >
          {stats.map((stat, i) => {
            const Icon = stat.icon;
            return (
              <motion.div
                key={i}
                className="text-center p-6 rounded-2xl bg-background border border-border/50 shadow-sm"
                initial={{ opacity: 0, y: 30 }}
                animate={statsInView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: i * 0.15, duration: 0.5 }}
              >
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mx-auto mb-3">
                  <Icon className="w-6 h-6 text-primary" />
                </div>
                <p className="text-3xl sm:text-4xl font-bold text-foreground tabular-nums mb-1">
                  <AnimatedCounter
                    end={stat.number}
                    suffix={stat.suffix}
                    duration={1500}
                  />
                </p>
                <p className="text-sm text-muted-foreground font-medium">
                  {stat.label}
                </p>
              </motion.div>
            );
          })}
        </div>

        {/* Business Growth */}
        <div
          ref={growthRef}
          className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center max-w-5xl mx-auto"
        >
          <div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={growthInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5 }}
            >
              <h3 className="text-2xl sm:text-3xl font-bold text-foreground mb-3 leading-tight">
                Grow Your Brokerage Business with{" "}
                <span className="gradient-text">PRObroker</span>
              </h3>
              <p className="text-muted-foreground text-sm leading-relaxed mb-5">
                PRObroker helps brokers find verified properties faster, close
                deals quicker, and scale their business.
              </p>
              <motion.div
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-8"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={growthInView ? { opacity: 1, scale: 1 } : {}}
                transition={{ delay: 0.3, duration: 0.4 }}
              >
                <TrendingUp className="w-4 h-4 text-primary" />
                <span className="text-sm font-semibold text-primary">
                  Up to 10x Business Growth
                </span>
              </motion.div>
            </motion.div>

            <div className="space-y-4">
              {benefits.map((b, i) => (
                <motion.div
                  key={i}
                  className="flex items-start gap-3 p-3 rounded-xl hover:bg-background/80 transition-colors"
                  initial={{ opacity: 0, x: -20 }}
                  animate={growthInView ? { opacity: 1, x: 0 } : {}}
                  transition={{ delay: 0.5 + i * 0.2, duration: 0.4 }}
                >
                  <CheckCircle2 className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-foreground leading-relaxed">
                    {b.text}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>

          <motion.div
            className="p-6 sm:p-8 rounded-2xl bg-background border border-border/50 shadow-sm"
            initial={{ opacity: 0, y: 30 }}
            animate={growthInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.2, duration: 0.5 }}
          >
            <div className="flex items-center gap-2 mb-6">
              <IndianRupee className="w-5 h-5 text-primary" />
              <span className="text-sm font-semibold text-foreground">
                Business Growth
              </span>
            </div>

            <div className="flex items-end justify-between gap-3 h-48 sm:h-56 mb-4">
              {[
                { h: 15, label: "1x" },
                { h: 30, label: "2x" },
                { h: 45, label: "3x" },
                { h: 60, label: "5x" },
                { h: 80, label: "7x" },
                { h: 100, label: "10x" },
              ].map((bar, i) => (
                <div
                  key={i}
                  className="flex-1 flex flex-col items-center gap-1 h-full"
                >
                  <div className="flex-1 w-full flex items-end">
                    <motion.div
                      className="w-full rounded-t-lg bg-gradient-to-t from-primary/40 to-primary relative"
                      initial={{ height: 0 }}
                      animate={growthInView ? { height: `${bar.h}%` } : {}}
                      transition={{
                        delay: 0.6 + i * 0.12,
                        duration: 0.6,
                        ease: "easeOut",
                      }}
                    >
                      {i === 5 && (
                        <motion.div
                          className="absolute -top-8 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground text-[10px] font-bold px-2.5 py-1 rounded-md whitespace-nowrap shadow-md"
                          initial={{ opacity: 0, y: 5 }}
                          animate={growthInView ? { opacity: 1, y: 0 } : {}}
                          transition={{ delay: 1.5 }}
                        >
                          🚀 10x
                        </motion.div>
                      )}
                    </motion.div>
                  </div>
                  <span className="text-[10px] font-medium text-muted-foreground">
                    {bar.label}
                  </span>
                </div>
              ))}
            </div>

            <p className="text-xs text-muted-foreground text-center">
              Your earnings grow with every deal —{" "}
              <span className="text-primary font-semibold">0% commission</span>
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default EcosystemAndCommissionSection;
